import type { Translate } from 'next-translate';
import React from 'react';
import { type DropzoneOptions } from 'react-dropzone';
import { Container } from '@/components/elements/Container';
import { useEmailFunnelFlow, useTranslationWithLocale } from '@/hooks';
import { GenerateLandingLinkService, GoogleService } from '@/services';
import type { LandingConfigItem } from '@/types';
import { GOOGLE_EVENTS } from '@/constants';
import { Accordion } from '../../blocks/Accordion';
import { DropzoneCtaButton } from '../../reusableSections';
import { EmailPopup } from '../EmailPopup';
import styles from './FAQuestions.module.scss';

interface FAQProps {
	data: LandingConfigItem['faq'];
	t: Translate;
	onClick?: () => void;
	funnelLinkService?: string;
	withDropzone?: boolean;
	dropzoneOptions?: DropzoneOptions;
	useEmailFlow?: boolean;
	/** Submit label for the email popup; defaults to the funnels' download wording */
	emailSubmitText?: string;
	/** Icon for the email popup's terms line */
	emailTermsIcon?: React.ReactNode;
	/** Inline nodes for the answers' placeholders, keyed by the placeholder */
	links?: Record<string, React.ReactNode>;
	/** Pages that already ask about cancellation in their own list opt out */
	withSubscriptionItem?: boolean;
	/** `main` — the main page's look: no CTA, Lato headings, filled open item */
	variant?: 'default' | 'main';
}
export const FAQ: React.FC<FAQProps> = ({
	data,
	t,
	onClick,
	funnelLinkService,
	withDropzone,
	dropzoneOptions,
	useEmailFlow = false,
	emailSubmitText,
	emailTermsIcon,
	links,
	withSubscriptionItem,
	variant = 'default',
}) => {
	const isMain = variant === 'main';
	const { t: tCommon } = useTranslationWithLocale('common');
	const { open: openEmailPopup, popupProps } = useEmailFunnelFlow({
		funnelLinkService,
		t,
		tCommon,
	});

	const goToWebProduct = () => {
		if (useEmailFlow) {
			openEmailPopup();
			return;
		}
		GoogleService.logEvent(GOOGLE_EVENTS.LANDING_PASSED);
		window.location.href =
			funnelLinkService || GenerateLandingLinkService.webApp();
	};
	const handleClick = () => {
		if (onClick) onClick();
		goToWebProduct();
	};

	const renderButton = () => (
		<DropzoneCtaButton
			withDropzone={!!withDropzone}
			dropzoneOptions={dropzoneOptions}
			text={t(data.buttonText)}
			onClick={handleClick}
			classes={{ defaultButton: styles['section__button'] }}
		/>
	);

	return (
		<section
			className={`${styles['section']}${
				isMain ? ` ${styles['section_main']}` : ''
			}`}
		>
			<Container className={`container ${styles['section__container']}`}>
				<div className={`${styles['section__top']}`}>
					<h2 className={`${styles['section__title']}`}>{t(data.title)}</h2>

					{!isMain && (
						<div className={`${styles['section__button-wrapper']}`}>
							{renderButton()}
						</div>
					)}
				</div>
				<Accordion
					items={data.list}
					t={t}
					links={links}
					withSubscriptionItem={withSubscriptionItem}
					variant={variant}
				/>
				{!isMain && (
					<div className={styles['section__button-wrapper-2']}>
						{renderButton()}
					</div>
				)}
			</Container>
			{useEmailFlow && (
				<EmailPopup
					{...popupProps}
					submitText={emailSubmitText}
					termsIcon={emailTermsIcon}
				/>
			)}
		</section>
	);
};
